<script lang="ts">
	import { format } from 'src/services/i18n';
	import { formatSnakeCaseToTitle } from 'src/helpers/utilities';
	import {
		convertSeconds,
		convertToSeconds,
		formatTwoDigits,
		formatTimeLabel,
		getTimeWheelTexts,
		getTimeWheelMsStep,
		getTimeWheelToggleText,
	} from 'src/helpers/timer_helpers';
	import {
		readWaveActuatorConfig,
		zwaveActuatorGetConfig,
		zwaveActuatorSetConfig,
	} from 'src/helpers/z_wave_actuator_config_helpers';
	//Design system components
	import { Button, Toggle, Text, toast, TimeWheel } from '@Shelly/shelly-design-system';
	import { onMount } from 'svelte';
	import type { Device } from 'src/types/Device';

	export let device: Device;

	const subsettings = ['auto_on', 'auto_off'] as const;

	let loading = { auto_on: false, auto_off: false };
	let min = 0;
	let max = 2147483;

	const syncTimersFromStore = () => {
		const settingsPerChannel = readWaveActuatorConfig(device);
		return {
			auto_on: Boolean(settingsPerChannel['auto_on']),
			auto_on_delay: Number(settingsPerChannel['auto_on_delay']) || 0,
			auto_off: Boolean(settingsPerChannel['auto_off']),
			auto_off_delay: Number(settingsPerChannel['auto_off_delay']) || 0,
		};
	};

	let timersValues = syncTimersFromStore();

	let lastCustomDelays = {
		auto_on_delay: timersValues.auto_on_delay > 0 ? timersValues.auto_on_delay : 60,
		auto_off_delay: timersValues.auto_off_delay > 0 ? timersValues.auto_off_delay : 60,
	};

	let selectedHour = { auto_on_delay: '00', auto_off_delay: '00' };
	let selectedMinute = { auto_on_delay: '00', auto_off_delay: '00' };
	let selectedSecond = { auto_on_delay: '00', auto_off_delay: '00' };
	let selectedMs = {
		auto_on_delay: undefined as string | undefined,
		auto_off_delay: undefined as string | undefined,
	};
	let maxTimes = convertSeconds(max);

	$: infoText = `${$format('timer_info_description', {
		values: {
			min: min,
			max: formatTimeLabel(
				maxTimes.hours,
				maxTimes.minutes,
				maxTimes.seconds,
				maxTimes.milliseconds,
			),
		},
	})}`;

	$: errors = {
		auto_on_error:
			convertToSeconds(
				selectedHour['auto_on_delay'],
				selectedMinute['auto_on_delay'],
				selectedSecond['auto_on_delay'],
				selectedMs['auto_on_delay'],
			) > max
				? infoText
				: '',
		auto_off_error:
			convertToSeconds(
				selectedHour['auto_off_delay'],
				selectedMinute['auto_off_delay'],
				selectedSecond['auto_off_delay'],
				selectedMs['auto_off_delay'],
			) > max
				? infoText
				: '',
	};

	onMount(async () => {
		await zwaveActuatorGetConfig(device);
		timersValues = syncTimersFromStore();
		lastCustomDelays = {
			auto_on_delay: timersValues.auto_on_delay > 0 ? timersValues.auto_on_delay : 60,
			auto_off_delay: timersValues.auto_off_delay > 0 ? timersValues.auto_off_delay : 60,
		};
		updateTimeValues('auto_on_delay');
		updateTimeValues('auto_off_delay');
	});

	const getTimeInSeconds = (type: 'auto_on_delay' | 'auto_off_delay') => {
		return convertToSeconds(
			selectedHour[type],
			selectedMinute[type],
			selectedSecond[type],
			selectedMs[type],
		);
	};

	const updateTimeValues = (type: 'auto_on_delay' | 'auto_off_delay') => {
		const { hours, minutes, seconds, milliseconds } = convertSeconds(timersValues[type] || 0);
		selectedHour[type] = formatTwoDigits(hours);
		selectedMinute[type] = formatTwoDigits(minutes);
		selectedSecond[type] = formatTwoDigits(seconds);
		selectedMs[type] = milliseconds ? formatTwoDigits(milliseconds) : undefined;
	};

	const set_auto = async (setting: 'auto_on' | 'auto_off') => {
		loading[setting] = true;

		const delaySeconds = getTimeInSeconds(`${setting}_delay`);
		timersValues[`${setting}_delay`] = delaySeconds;

		const response = await zwaveActuatorSetConfig(device, {
			[setting]: timersValues[setting],
			[`${setting}_delay`]: delaySeconds,
		});

		if (response?.isok) {
			if (!timersValues[setting]) {
				toast.open({
					type: 'success',
					title: $format('auto_turn_successfully_disable', {
						values: { auto_turn_state: $format(setting) },
					}),
				});
			} else {
				const toastKey = `${setting}_set_toast`;
				const formattedTime = formatTimeLabel(
					selectedHour[`${setting}_delay`],
					selectedMinute[`${setting}_delay`],
					selectedSecond[`${setting}_delay`],
					selectedMs[`${setting}_delay`],
				);
				toast.open({
					type: 'success',
					title: `${$format(toastKey)} ${formattedTime}.`,
				});
			}
		} else {
			const errorMessage = (response as { errors?: { message?: string } })?.errors?.message;
			toast.open({
				type: 'danger',
				title: errorMessage || $format('timer_err_toast'),
			});
		}
		loading[setting] = false;
	};

	const handle_toggler = async (setting: 'auto_on' | 'auto_off') => {
		if (loading[setting]) return;

		timersValues[setting] = !timersValues[setting];

		const delayKey = `${setting}_delay` as 'auto_on_delay' | 'auto_off_delay';
		if (!timersValues[setting]) {
			if (timersValues[delayKey] > 0) lastCustomDelays[delayKey] = timersValues[delayKey];
		} else if (!timersValues[delayKey] || timersValues[delayKey] <= 0) {
			timersValues[delayKey] = lastCustomDelays[delayKey] > 0 ? lastCustomDelays[delayKey] : 60;
			updateTimeValues(delayKey);
		}

		await set_auto(setting);
	};
</script>

{#each subsettings as setting, index}
	{#if index > 0}
		<hr class="mt-l mb-l" />
	{/if}

	<Toggle
		size="m"
		checked={timersValues[setting]}
		disabled={loading[setting]}
		on:click={() => handle_toggler(setting)}
		id="toggle-timer-z-{setting}"
		aria-label={formatSnakeCaseToTitle($format(setting))}
	>
		<Text element="span" styleType="controls-small" class="text-white">
			{formatSnakeCaseToTitle($format(setting))}
		</Text>
	</Toggle>

	<Text styleType="tiny" class="text-white" style="margin-top: 2px">
		{$format(`${setting}_toggle_info`, {
			default:
				setting === 'auto_on'
					? 'Turns the device on automatically after the selected time'
					: 'Turns the device off automatically after the selected time',
		})}
	</Text>

	{#if timersValues[setting]}
		<Text styleType="controls-small" class="mt-s-plus mb-s">
			{$format(`${setting}_time_label`, {
				default:
					setting === 'auto_on' ? 'Turn the device on after' : 'Turn the device off after',
			})}
			<Text element="span" styleType="body-small" class="text-primary text-bold">
				{formatTimeLabel(
					selectedHour[`${setting}_delay`],
					selectedMinute[`${setting}_delay`],
					selectedSecond[`${setting}_delay`],
					selectedMs[`${setting}_delay`],
				)}
			</Text>
		</Text>

		<TimeWheel
			id="timewheel-wave-{setting}"
			a11yProps={{
				inputAriaLabel: $format('label_time_wheel_input', {
					default: 'Time wheel input',
				}),
				buttonAriaLabel: $format('label_time_wheel_button', {
					default: 'Time wheel button',
				}),
				secondsColumnAriaLabel: $format('label_time_wheel_seconds_column', {
					default: 'Time wheel seconds column',
				}),
				secondPrefixAriaLabel: $format('label_time_wheel_second_prefix', {
					default: 'Time wheel second prefix',
				}),
				msColumnAriaLabel: $format('label_time_wheel_ms_column', {
					default: 'Time wheel ms column',
				}),
				msPrefixAriaLabel: $format('label_time_wheel_ms_prefix', {
					default: 'Time wheel ms prefix',
				}),
				hoursColumnAriaLabel: $format('label_time_wheel_hours_column', {
					default: 'Time wheel hours column',
				}),
				hourPrefixAriaLabel: $format('label_time_wheel_hour_prefix', {
					default: 'Time wheel hour prefix',
				}),
				minutesColumnAriaLabel: $format('label_time_wheel_minutes_column', {
					default: 'Time wheel minutes column',
				}),
				minutePrefixAriaLabel: $format('label_time_wheel_minute_prefix', {
					default: 'Time wheel minute prefix',
				}),
			}}
			class="mb-s"
			bind:selectedHour={selectedHour[`${setting}_delay`]}
			bind:selectedMinute={selectedMinute[`${setting}_delay`]}
			bind:selectedSecond={selectedSecond[`${setting}_delay`]}
			bind:selectedMs={selectedMs[`${setting}_delay`]}
			maxHours={maxTimes.hours}
			msToggleText={getTimeWheelToggleText()}
			info={infoText}
			error={errors[`${setting}_error`]}
			texts={getTimeWheelTexts()}
			msStep={getTimeWheelMsStep(device.type)}
		/>

		<Button
			loading={loading[setting]}
			disabled={loading[setting] || errors[`${setting}_error`]}
			size="m"
			width="full"
			on:click={() => set_auto(setting)}>{$format('button_save')}</Button
		>
	{/if}
{/each}
